import { isVNode } from 'vue'
import type { ComponentInternalInstance, ComponentPublicInstance, EffectScope, VNode } from 'vue'

import { PAGE_FADING_SELECTOR } from '@layers/web/constants/page-transition.ts'

/* The scope that owns a component's render effect and watchers; Vue keeps it out of the published typings */
type ScopedComponent = ComponentInternalInstance & { scope: EffectScope }

const rootElementOf = (instance: ComponentInternalInstance): Element | null => {
  const node = instance.subTree.el as Node | null

  if (!node) {
    return null
  }

  return node instanceof Element ? node : node.parentElement
}

/* The header, the sidebar and the footer: neither inside a fading region nor wrapping one */
const isChromeComponent = (instance: ComponentInternalInstance): boolean => {
  const root = rootElementOf(instance)

  return !!root && !root.closest(PAGE_FADING_SELECTOR) && !root.querySelector(PAGE_FADING_SELECTOR)
}

const collectPageScopes = (vnode: VNode, scopes: EffectScope[]): void => {
  if (vnode.component) {
    if (!isChromeComponent(vnode.component)) {
      scopes.push((vnode.component as ScopedComponent).scope)
    }

    collectPageScopes(vnode.component.subTree, scopes)
    return
  }

  if (vnode.suspense?.activeBranch) {
    collectPageScopes(vnode.suspense.activeBranch, scopes)
    return
  }

  if (!Array.isArray(vnode.children)) {
    return
  }

  vnode.children.forEach((child) => {
    if (isVNode(child)) {
      collectPageScopes(child, scopes)
    }
  })
}

/**
 * Pauses the render effects and watchers of a page's components, except those of the header, the sidebar and the
 * footer, so the page keeps showing what it shows while another page's setup writes the stores it renders from.
 * Unmounting a paused component needs no resuming.
 */
const freezePage = (page: ComponentPublicInstance): EffectScope[] => {
  const scopes: EffectScope[] = []

  collectPageScopes(page.$.vnode, scopes)
  scopes.forEach((scope) => scope.pause())

  return scopes
}

const unfreezePage = (scopes: EffectScope[]): void => {
  scopes.forEach((scope) => scope.resume())
}

export { freezePage, unfreezePage }
